import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { MapPin, ChevronDown, Check, Star, LayoutGrid, Loader2 } from 'lucide-react';

// Caché a nivel módulo para no llamar getMyOrgLocations en cada navegación.
// Las sedes de una cadena cambian poco; TTL corto evita datos rancios.
let sedesCache = { orgId: null, data: null, ts: 0 };
const CACHE_TTL_MS = 60 * 1000;

/**
 * Indicador + selector de sede en la barra superior del dashboard.
 * Solo se muestra a miembros de una cadena (hasOrg). Permite ver en qué sede
 * estás y saltar a otra sin pasar por la pantalla "Mis sedes".
 */
const BranchSwitcher = () => {
  const navigate = useNavigate();
  const { hasOrg, orgId, restaurantId, restaurantProfile, switchRestaurant } = useAuth();

  const [open, setOpen] = useState(false);
  const [sedes, setSedes] = useState(() =>
    sedesCache.data && sedesCache.orgId === orgId ? sedesCache.data : []
  );
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);
  const containerRef = useRef(null);

  const loadSedes = useCallback(async (force = false) => {
    if (!hasOrg) return;
    const fresh = sedesCache.data
      && sedesCache.orgId === orgId
      && Date.now() - sedesCache.ts < CACHE_TTL_MS;
    if (fresh && !force) {
      setSedes(sedesCache.data);
      return;
    }
    try {
      setLoading(true);
      const fn = httpsCallable(functions, 'restaurants-endpoints-getMyOrgLocations');
      const res = await fn();
      const data = res.data?.locations || [];
      sedesCache = { orgId, data, ts: Date.now() };
      setSedes(data);
    } catch (err) {
      console.error('[BranchSwitcher] Error cargando sedes:', err);
    } finally {
      setLoading(false);
    }
  }, [hasOrg, orgId]);

  // Cargar al montar (para que el chip muestre la sede correcta y la marca de principal).
  useEffect(() => {
    if (hasOrg) loadSedes();
  }, [hasOrg, loadSedes]);

  // Cerrar el dropdown al hacer click fuera.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [open]);

  // Sede actual: priorizar la lista cargada; fallback al perfil ya cargado.
  const current = useMemo(() => {
    const fromList = sedes.find((s) => s.id === restaurantId);
    if (fromList) return fromList;
    if (restaurantProfile?.id === restaurantId) {
      return {
        id: restaurantId,
        locationName: restaurantProfile.locationName || restaurantProfile.name,
        isPrimaryLocation: restaurantProfile.isPrimaryLocation === true,
      };
    }
    return null;
  }, [sedes, restaurantId, restaurantProfile]);

  if (!hasOrg) return null;

  const currentLabel = current?.locationName || 'Sede';

  const handleSelect = async (sede) => {
    if (sede.id === restaurantId) {
      setOpen(false);
      return;
    }
    try {
      setSwitching(true);
      await switchRestaurant(sede.id);
      setOpen(false);
      navigate(`/${sede.id}`);
    } finally {
      setSwitching(false);
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => { setOpen((v) => !v); if (!open) loadSedes(); }}
        className="flex items-center gap-1.5 bg-surface-3/50 hover:bg-surface-4/50 text-content-secondary hover:text-content-primary px-2.5 md:px-3 py-2 rounded-lg border border-line-strong/50 hover:border-line-active/50 transition-all duration-200 max-w-[42vw] sm:max-w-none"
        title="Sede actual — click para cambiar"
      >
        {current?.isPrimaryLocation ? (
          <Star size={15} className="text-kumia-green flex-shrink-0" />
        ) : (
          <MapPin size={15} className="text-kumia-green flex-shrink-0" />
        )}
        <span className="text-sm font-medium truncate">{currentLabel}</span>
        <ChevronDown size={14} className={`flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute left-0 mt-2 w-64 max-h-[70vh] overflow-y-auto bg-surface-base border border-line-strong rounded-xl shadow-2xl z-[120] py-1.5">
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-content-subtle">
            Tus sedes
          </div>

          {loading && sedes.length === 0 && (
            <div className="flex items-center justify-center py-4">
              <Loader2 size={18} className="animate-spin text-kumia-green" />
            </div>
          )}

          {sedes.map((sede) => {
            const isCurrent = sede.id === restaurantId;
            return (
              <button
                key={sede.id}
                onClick={() => handleSelect(sede)}
                disabled={switching}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors disabled:opacity-50 ${
                  isCurrent ? 'bg-kumia-green/10' : 'hover:bg-surface-3'
                }`}
              >
                {sede.isPrimaryLocation ? (
                  <Star size={15} className="text-kumia-green flex-shrink-0" />
                ) : (
                  <MapPin size={15} className="text-content-subtle flex-shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <div className={`text-sm truncate ${isCurrent ? 'text-kumia-green font-medium' : 'text-content-primary'}`}>
                    {sede.locationName || sede.name}
                  </div>
                  {sede.isPrimaryLocation && (
                    <div className="text-[10px] text-content-subtle">Casa matriz</div>
                  )}
                </div>
                {isCurrent && <Check size={15} className="text-kumia-green flex-shrink-0" />}
              </button>
            );
          })}

          <div className="border-t border-line-subtle mt-1.5 pt-1.5">
            <button
              onClick={() => { setOpen(false); navigate('/chain'); }}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm text-content-muted hover:text-content-primary hover:bg-surface-3 transition-colors"
            >
              <LayoutGrid size={15} className="flex-shrink-0" />
              Ver todas las sedes
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BranchSwitcher;
